import { z } from 'zod';
import { pipe, Option, Array as A } from 'effect';
import type { MessageDescriptor } from 'react-intl';
import type { PaymentMetaOptionDetails } from '@/domain/payments/types';
import type { BeneficiaryFieldOption } from '@/domain/payments/schemas';
import { splitIsraeliIban } from '@/lib/iban';
import { BENEFICIARY_FIELD_CONFIG, type FieldValidation } from '@/pages/dokkaPay/pages/payment/components/PaymentDetails/components/PaymentInfo/components/BeneficiaryModal/config';
import type { BeneficiaryField, FormValues } from '@/pages/dokkaPay/pages/payment/components/PaymentDetails/components/PaymentInfo/components/BeneficiaryModal/types';

type FormatMessage = (msg: MessageDescriptor, values?: Record<string, string | number>) => string;

function applyStringValidation(schema: z.ZodString, v: FieldValidation, formatMessage: FormatMessage): z.ZodString {
  let s = schema;
  if (v.minLength)
    s = s.min(v.minLength, formatMessage({ id: 'beneficiary.validation.minLength', defaultMessage: 'Min {min} characters' }, { min: v.minLength }));
  if (v.maxLength)
    s = s.max(v.maxLength, formatMessage({ id: 'beneficiary.validation.maxLength', defaultMessage: 'Max {max} characters' }, { max: v.maxLength }));
  v.patterns?.forEach(({ pattern, message }) => {
    s = s.regex(pattern, formatMessage(message));
  });
  return s;
}

// A field is shown / validated / submitted only when its `visibleWhen` guard is
// satisfied by the current form values. No guard → always visible.
export function isFieldVisible(field: BeneficiaryField, values: FormValues): boolean {
  if (!field.visibleWhen) return true;
  return values[field.visibleWhen.field] === field.visibleWhen.equals;
}

export function getVisibleFields(fields: BeneficiaryField[], values: FormValues): BeneficiaryField[] {
  return fields.filter((field) => isFieldVisible(field, values));
}

// The parent field whose value scopes this field's options, if any. `dependsOn[0]`
// is both the parent's name and the key each option carries its parent value
// under, so one lookup answers both questions.
export function getParentFieldName(field: BeneficiaryField): string | undefined {
  return field.dependsOn?.[0];
}

// Options offered for a field, scoped to its parent's current value. Driven
// entirely by `dependsOn` — no knowledge of which fields depend on which.
// A field with no parent returns all its options; a field whose parent has no
// value yet returns none (there is nothing to scope by).
export function getScopedOptions(field: BeneficiaryField, values: FormValues): BeneficiaryFieldOption[] {
  const options = field.options ?? [];

  return pipe(
    Option.fromNullable(getParentFieldName(field)),
    Option.match({
      // No parent — nothing to scope by, so every option is on offer.
      onNone: () => options,
      onSome: (parentName) =>
        pipe(
          Option.fromNullable(values[parentName]),
          Option.filter((value): value is string => typeof value === 'string' && value.length > 0),
          Option.map((parentValue) =>
            pipe(
              options,
              A.filter((option) => option[parentName] === parentValue),
            ),
          ),
          // Parent not chosen yet — offer nothing rather than everything.
          Option.getOrElse<BeneficiaryFieldOption[]>(() => []),
        ),
    }),
  );
}

// Payload = only currently-visible fields, keyed by their own `name`. Hidden
// conditional fields (guarded by `visibleWhen`) must not leak in.
export function pickVisibleValues(values: FormValues, visibleFields: BeneficiaryField[]): FormValues {
  const visibleNames = new Set(visibleFields.map((f) => f.name));
  return Object.fromEntries(Object.entries(values).filter(([key]) => visibleNames.has(key)));
}

export function buildZodSchema(fields: BeneficiaryField[], formatMessage: FormatMessage) {
  const required = formatMessage({ id: 'input.required', defaultMessage: 'Required' });
  const invalidEmail = formatMessage({ id: 'message.invalid_email_address', defaultMessage: 'Invalid email address' });

  const fieldSchema = (field: BeneficiaryField): z.ZodTypeAny => {
    const v = BENEFICIARY_FIELD_CONFIG[field.name]?.validation;
    switch (field.type) {
      case 'multi-select':
        return z.array(z.string()).min(1, required);
      case 'email':
        return applyStringValidation(z.string().min(1, required).email(invalidEmail), v ?? {}, formatMessage);
      // Read-only assembled IBAN preview: the value is derived from its parts,
      // which carry the real format rules. Here we only assert it is non-empty
      // (i.e. all parts are filled) — resolving by field TYPE.
      case 'iban':
        return z.string().min(1, required);
      default:
        return applyStringValidation(z.string().min(1, required), v ?? {}, formatMessage);
    }
  };

  const shape = fields.reduce<Record<string, z.ZodTypeAny>>((acc, field) => {
    acc[field.name] = fieldSchema(field);
    return acc;
  }, {});

  return z.object({ ...shape, __isDefault: z.enum(['true', 'false']) });
}

// When editing, the backend returns only the assembled IBAN `account_number`
// (IL + check + 19-digit BBAN). Split it back into the part fields so they
// prefill and the preview reassembles the same value. `computedFrom` order is
// [bankCode, branchCode, accountNumber]; no-op when nothing is stored or the
// parts already came from the backend.
const ACCOUNT_PART_COUNT = 3;
function applyAccountDecomposition(fields: BeneficiaryField[], values: FormValues): FormValues {
  const composite = fields.find(
    (f) => f.type === 'iban' && (f.computedFrom?.length ?? 0) >= ACCOUNT_PART_COUNT && isFieldVisible(f, values),
  );
  if (!composite?.computedFrom) return values;

  const assembled = values[composite.name];
  if (typeof assembled !== 'string' || !assembled) return values;

  const partsAlreadySet = composite.computedFrom.some((name) => {
    const value = values[name];
    return typeof value === 'string' && value.length > 0;
  });
  if (partsAlreadySet) return values;

  const [bankCodeName, branchCodeName, accountNumberName] = composite.computedFrom;
  const parts = splitIsraeliIban(assembled);
  return {
    ...values,
    [bankCodeName]: parts.bankCode,
    [branchCodeName]: parts.branchCode,
    [accountNumberName]: parts.accountNumber,
  };
}

export function buildDefaultValues(fields: BeneficiaryField[], initialValues?: PaymentMetaOptionDetails): FormValues {
  const base = fields.reduce<FormValues>((acc, field) => {
    if (field.type === 'multi-select') {
      acc[field.name] = Array.isArray(field.defaultValue)
        ? field.defaultValue
        : field.defaultValue
          ? [field.defaultValue as string]
          : [];
    } else {
      acc[field.name] =
        typeof field.defaultValue === 'string'
          ? field.defaultValue
          : Array.isArray(field.defaultValue) && field.defaultValue.length > 0
            ? (field.defaultValue[0] as string)
            : '';
    }
    return acc;
  }, {});

  const definedInitial = Object.fromEntries(
    Object.entries(initialValues ?? {}).filter((entry): entry is [string, string] => entry[1] !== undefined),
  );

  return applyAccountDecomposition(fields, { ...base, ...definedInitial });
}
